import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, User, MapPin, CreditCard } from 'lucide-react';

const EditOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [addons, setAddons] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    contract_type: '24m',
    selectedAddons: [],
    customer: {
      salutation: 'Herr',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      birth_date: '',
      company_name: '',
      service_street: '',
      service_house_number: '',
      service_postal_code: '',
      service_city: '',
      billing_same_as_service: true,
      billing_street: '',
      billing_house_number: '',
      billing_postal_code: '',
      billing_city: ''
    },
    sepa: {
      account_holder: '',
      iban: ''
    },
    preferred_date: 'sofort',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [orderRes, productsRes, addonsRes] = await Promise.all([
        api.getOrder(orderId),
        api.getProducts(),
        api.getAddons()
      ]);
      
      const orderData = orderRes.data;
      
      // Check if order can be edited
      if (orderData.status !== 'pending') {
        toast.error('Diese Bestellung kann nicht mehr bearbeitet werden');
        navigate(`/orders/${orderId}`);
        return;
      }
      
      setOrder(orderData);
      setProducts(productsRes.data);
      setAddons(addonsRes.data);
      
      // Populate form with existing data
      setFormData({
        product_id: orderData.product_id,
        contract_type: orderData.contract_type,
        selectedAddons: orderData.addons || [],
        customer: orderData.customer,
        sepa: orderData.sepa,
        preferred_date: orderData.preferred_date || 'sofort',
        notes: orderData.notes || ''
      });
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellung');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCustomerChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customer: { ...prev.customer, [field]: value }
    }));
  };

  const handleSepaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      sepa: { ...prev.sepa, [field]: value }
    }));
  };

  const handleAddonToggle = (addonId) => {
    setFormData(prev => ({
      ...prev,
      selectedAddons: prev.selectedAddons.includes(addonId)
        ? prev.selectedAddons.filter(id => id !== addonId)
        : [...prev.selectedAddons, addonId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.updateOrder(orderId, {
        product_id: formData.product_id,
        contract_type: formData.contract_type,
        addons: formData.selectedAddons,
        customer: formData.customer,
        sepa: formData.sepa,
        preferred_date: formData.preferred_date,
        notes: formData.notes
      });
      
      toast.success('Bestellung aktualisiert');
      navigate(`/orders/${orderId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(`/orders/${orderId}`)}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Bestellung #{order?.order_number} bearbeiten
          </h1>
          <p className="text-slate-500">Ändern Sie die Bestelldaten vor dem E-Mail-Versand</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Product Selection */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Produkt & Vertrag</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Produkt</Label>
                <Select 
                  value={formData.product_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Produkt wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - {product.monthly_price.toFixed(2)} €/Monat
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vertragslaufzeit</Label>
                <Select 
                  value={formData.contract_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, contract_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24m">24 Monate Laufzeit</SelectItem>
                    <SelectItem value="flex">Ohne Mindestlaufzeit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {addons.length > 0 && (
              <div className="space-y-2">
                <Label>Zusatzoptionen</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {addons.map(addon => (
                    <div key={addon.id} className="flex items-center gap-2 p-2 border rounded">
                      <Checkbox
                        checked={formData.selectedAddons.includes(addon.id)}
                        onCheckedChange={() => handleAddonToggle(addon.id)}
                      />
                      <span className="text-sm">{addon.name} (+{addon.monthly_price.toFixed(2)} €/Monat)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Data */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#F47920]" />
              Kundendaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Anrede</Label>
                <Select 
                  value={formData.customer.salutation} 
                  onValueChange={(value) => handleCustomerChange('salutation', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Firma">Firma</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vorname</Label>
                <Input
                  value={formData.customer.first_name}
                  onChange={(e) => handleCustomerChange('first_name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname</Label>
                <Input
                  value={formData.customer.last_name}
                  onChange={(e) => handleCustomerChange('last_name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Geburtsdatum</Label>
                <Input
                  type="date"
                  value={formData.customer.birth_date}
                  onChange={(e) => handleCustomerChange('birth_date', e.target.value)}
                  required
                />
              </div>
            </div>
            
            {formData.customer.salutation === 'Firma' && (
              <div className="space-y-2">
                <Label>Firmenname</Label>
                <Input
                  value={formData.customer.company_name}
                  onChange={(e) => handleCustomerChange('company_name', e.target.value)}
                />
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={formData.customer.email}
                  onChange={(e) => handleCustomerChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.customer.phone}
                  onChange={(e) => handleCustomerChange('phone', e.target.value)}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Address */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#F47920]" />
              Anschlussadresse
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label>Straße</Label>
                <Input
                  value={formData.customer.service_street}
                  onChange={(e) => handleCustomerChange('service_street', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Hausnummer</Label>
                <Input
                  value={formData.customer.service_house_number}
                  onChange={(e) => handleCustomerChange('service_house_number', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>PLZ</Label>
                <Input
                  value={formData.customer.service_postal_code}
                  onChange={(e) => handleCustomerChange('service_postal_code', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ort</Label>
              <Input
                value={formData.customer.service_city}
                onChange={(e) => handleCustomerChange('service_city', e.target.value)}
                required
              />
            </div>
            
            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                checked={formData.customer.billing_same_as_service}
                onCheckedChange={(checked) => handleCustomerChange('billing_same_as_service', checked)}
              />
              <Label className="cursor-pointer">Rechnungsadresse ist gleich der Anschlussadresse</Label>
            </div>
            
            {!formData.customer.billing_same_as_service && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Rechnungsadresse</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Straße</Label>
                    <Input
                      value={formData.customer.billing_street}
                      onChange={(e) => handleCustomerChange('billing_street', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hausnummer</Label>
                    <Input
                      value={formData.customer.billing_house_number}
                      onChange={(e) => handleCustomerChange('billing_house_number', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PLZ</Label>
                    <Input
                      value={formData.customer.billing_postal_code}
                      onChange={(e) => handleCustomerChange('billing_postal_code', e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Ort</Label>
                  <Input
                    value={formData.customer.billing_city}
                    onChange={(e) => handleCustomerChange('billing_city', e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SEPA Data */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#F47920]" />
              SEPA-Lastschrift
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kontoinhaber</Label>
                <Input
                  value={formData.sepa.account_holder}
                  onChange={(e) => handleSepaChange('account_holder', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={formData.sepa.iban}
                  onChange={(e) => handleSepaChange('iban', e.target.value.toUpperCase())}
                  placeholder="DE00 0000 0000 0000 0000 00"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Notizen</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Interne Notizen zur Bestellung..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/orders/${orderId}`)}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            className="bg-[#F47920] hover:bg-[#E06810] gap-2"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Änderungen speichern
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditOrder;
